/**
 * The Artifacts gallery viewer — the shared `@4f/ui` `Lightbox` over the
 * {@link GalleryStore}'s current asset, with swipe/next/prev (only when she has
 * more than one), a download action, and set-as-avatar. Presentational: the store
 * owns the list + cursor + save round-trip; this maps the current asset to a
 * lightbox item and the two domain actions into the primitive's generic toolbar.
 */
import { Lightbox, type LightboxAction, useT } from '@4f/ui';
import { createMemo, type JSX, Show } from 'solid-js';
import type { GalleryStore } from '../../stores/gallery.ts';
import { downloadFileName, toLightboxItem } from './lightbox-model.ts';

export interface ArtifactsGalleryProps {
  readonly store: GalleryStore;
  /** Her name — the lightbox's accessible label for each item. */
  readonly companionName: string;
  /** Download the current asset (the shell owns the browser navigation). */
  onDownload(url: string, filename: string): void;
  /** Promote the current asset to her avatar (the shell reloads the switcher + toasts). */
  onSetAvatar(): void;
}

export function ArtifactsGallery(props: ArtifactsGalleryProps): JSX.Element {
  const { t } = useT();

  const item = createMemo(() => {
    const asset = props.store.current();
    return asset ? toLightboxItem(asset, props.companionName) : null;
  });
  const hasMany = createMemo(() => props.store.items().length > 1);

  const download = (): void => {
    const asset = props.store.current();
    if (asset?.url) {
      props.onDownload(asset.url, downloadFileName(asset));
    }
  };

  const actions = createMemo<LightboxAction[]>(() => {
    const asset = props.store.current();
    if (!asset) {
      return [];
    }
    const list: LightboxAction[] = [];
    if (asset.url) {
      list.push({ iconName: 'download', label: t('gallery.actions.download'), onSelect: download });
    }
    list.push({
      iconName: 'user',
      label: t('surface.setAsAvatar'),
      onSelect: () => props.onSetAvatar(),
      disabled: props.store.savingAvatar(),
    });
    return list;
  });

  return (
    <Show when={props.store.isOpen() && item()}>
      {(currentItem) => (
        <Show
          when={hasMany()}
          fallback={
            <Lightbox
              open
              item={currentItem()}
              onClose={() => props.store.close()}
              closeLabel={t('surface.close')}
              actions={actions()}
            />
          }
        >
          <Lightbox
            open
            item={currentItem()}
            onClose={() => props.store.close()}
            closeLabel={t('surface.close')}
            actions={actions()}
            onPrev={() => props.store.prev()}
            prevLabel={t('surface.prev')}
            onNext={() => props.store.next()}
            nextLabel={t('surface.next')}
          />
        </Show>
      )}
    </Show>
  );
}
